import React, { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { formatCount } from "../../charts/charts.utils";
import NotFilterableByOfferHint from "../../charts/NotFilterableByOfferHint";
import StatCard from "../../kpi/StatCard";
import {
  offersPurchasesQuery,
  offersSalesQuery,
  offersSubscriptionsQuery,
} from "../offerAnalytics.queries";
import {
  filterByOffer,
  summarizePurchases,
  sumNewSubscriptions,
} from "../offerAnalytics.utils";

import type { JSX } from "react";

import type { OfferAnalyticsFilterProps } from "../offerAnalytics.types";

function SubscriptionsKpiGrid({
  filters,
}: OfferAnalyticsFilterProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t, i18n } = useTranslation("editor-analytics");
  const lang = i18n.language;

  const salesKpiResult = useQuery(offersSalesQuery(projectSlug));
  const subscriptionsResult = useQuery(
    offersSubscriptionsQuery(projectSlug, filters),
  );
  const purchasesResult = useQuery(offersPurchasesQuery(projectSlug, filters));

  const salesKpi = salesKpiResult.data?.data[0];
  const newSubscriptionTotal = useMemo(
    () => sumNewSubscriptions(subscriptionsResult.data?.data ?? []),
    [subscriptionsResult.data],
  );
  const salesSummary = useMemo(
    () =>
      summarizePurchases(
        filterByOffer(
          purchasesResult.data?.data ?? [],
          filters.offerUuid,
          (purchase) => purchase.offer_uuid,
        ),
      ),
    [purchasesResult.data, filters.offerUuid],
  );
  const asOfTodayFooter = filters.offerUuid ? (
    <NotFilterableByOfferHint />
  ) : undefined;

  return (
    <div className="grid grid-cols-2 gap-4 @2xl:grid-cols-3 @5xl:grid-cols-5">
      <StatCard
        footer={asOfTodayFooter}
        label={t("offerAnalytics.kpi.activeSvod")}
        query={salesKpiResult}
        scope="asOfToday"
        value={formatCount(salesKpi?.subscriptions.svod ?? 0, lang)}
      />
      <StatCard
        footer={asOfTodayFooter}
        label={t("offerAnalytics.kpi.activeFreecopy")}
        query={salesKpiResult}
        scope="asOfToday"
        value={formatCount(salesKpi?.subscriptions.free_copy ?? 0, lang)}
      />
      <StatCard
        footer={asOfTodayFooter}
        label={t("offerAnalytics.kpi.activeBundle")}
        query={salesKpiResult}
        scope="asOfToday"
        value={formatCount(salesKpi?.sales.tvod_bundle ?? 0, lang)}
      />
      <StatCard
        label={t("offerAnalytics.kpi.newSubscriptions")}
        query={subscriptionsResult}
        scope="period"
        value={formatCount(newSubscriptionTotal, lang)}
      />
      <StatCard
        label={t("offerAnalytics.kpi.sales")}
        query={purchasesResult}
        scope="period"
        value={formatCount(salesSummary.total, lang)}
        footer={t("offerAnalytics.kpi.salesFooter", {
          direct: formatCount(salesSummary.direct, lang),
          gifts: formatCount(salesSummary.gifts, lang),
        })}
      />
    </div>
  );
}

export default SubscriptionsKpiGrid;
